/**
 * Copyright 2026 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  FileText, 
  AlertCircle, 
  CheckCircle, 
  Loader, 
  Download, 
  Settings, 
  Clock, 
  GitCommit,
  FilePlus,
  FileMinus,
  FileEdit,
  ChevronRight
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { getRepoContext } from '../../utils/repoContext';
import { getCommitHistory, getCommitFiles, getCommitFileDiff, getWorktrees } from '../../lib/api';
import { 
  exportDiffReportToPdf, 
  type DiffReportCommit, 
  type DiffReportFile,
  type DiffReportOptions 
} from '../../utils/pdfDiffExport';

interface DiffReportDialogProps {
  onClose: () => void;
  onExportComplete: () => void;
}

interface CommitEntry {
  hash: string;
  shortHash: string;
  message: string;
  author: {
    name: string;
    email: string;
  };
  date: string;
  pushed?: boolean | null;
}

function formatCommitDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getFileStatusIcon(status: string) {
  switch (status) {
    case 'added':
      return <FilePlus size={14} className="text-green-500" />;
    case 'deleted':
      return <FileMinus size={14} className="text-red-500" />;
    case 'modified':
      return <FileEdit size={14} className="text-yellow-500" />;
    default:
      return <FileText size={14} className="text-gray-500" />;
  }
}

export function DiffReportDialog({ onClose, onExportComplete }: DiffReportDialogProps) {
  const { activeProduct, activeRepoUrl } = useApp();
  
  const [step, setStep] = useState<'select' | 'preview' | 'options' | 'exporting' | 'complete'>('select');
  const [commits, setCommits] = useState<CommitEntry[]>([]);
  const [loadingCommits, setLoadingCommits] = useState(true);
  const [selectedCommit, setSelectedCommit] = useState<CommitEntry | null>(null);
  const [commitFiles, setCommitFiles] = useState<Array<{ path: string; status: string }>>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesWithDiffs, setFilesWithDiffs] = useState<DiffReportFile[]>([]);
  const [loadingDiffs, setLoadingDiffs] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ 
    filename: string; 
    userStoryCount: number; 
    issueCount: number 
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [options, setOptions] = useState<DiffReportOptions>({
    title: 'Diff Report',
    productName: activeProduct?.title || '',
    pageSize: 'a4',
    orientation: 'portrait',
    includeRawDiff: true,
    includeFileSummary: true,
  });

  const repoContext = useMemo(
    () => getRepoContext(activeProduct ?? null, activeRepoUrl),
    [activeProduct, activeRepoUrl]
  );

  const [worktreePath, setWorktreePath] = useState<string | null>(null);

  useEffect(() => {
    const loadWorktreesAndCommits = async () => {
      if (!repoContext.repoUrl) {
        setError('No repository configured');
        setLoadingCommits(false);
        return;
      }

      try {
        setLoadingCommits(true);
        setError(null);
        
        // First, get the worktrees to find the main velocity worktree path
        const worktreesResult = await getWorktrees(repoContext.repoUrl, 'github');
        
        if (!worktreesResult.success || !worktreesResult.worktrees) {
          throw new Error(worktreesResult.error || 'Failed to load worktrees');
        }
        
        // Find the main velocity worktree
        const mainWorktree = worktreesResult.worktrees.find(w => w.path.endsWith('/velocity'));
        const wkPath = mainWorktree?.path || worktreesResult.worktrees[0]?.path;
        
        if (!wkPath) {
          throw new Error('No worktree found for this repository');
        }
        
        setWorktreePath(wkPath);
        
        // Now load the commit history
        const result = await getCommitHistory(repoContext.repoUrl, wkPath, 50);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to load commit history');
        }
        
        setCommits(result.commits || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load commits');
      } finally {
        setLoadingCommits(false);
      }
    };

    loadWorktreesAndCommits();
  }, [repoContext.repoUrl]);

  const handleSelectCommit = async (commit: CommitEntry) => {
    if (!repoContext.repoUrl || !worktreePath) return;
    
    setSelectedCommit(commit);
    setLoadingFiles(true);
    setError(null);
    
    try {
      const result = await getCommitFiles(repoContext.repoUrl, worktreePath, commit.hash);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to load commit files');
      }
      
      setCommitFiles(result.files || []);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load commit files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleProceedToOptions = async () => {
    if (!selectedCommit || !repoContext.repoUrl || !worktreePath) return;
    
    setLoadingDiffs(true);
    setError(null);
    
    try {
      const productDataFiles = commitFiles.filter(f => 
        f.path.includes('data/product/') || 
        f.path.includes('user-stories') ||
        f.path.includes('issues.md')
      );
      
      const filesWithDiffContent: DiffReportFile[] = [];
      
      for (const file of productDataFiles) {
        const diffResult = await getCommitFileDiff(
          repoContext.repoUrl,
          worktreePath,
          selectedCommit.hash,
          file.path
        );
        
        filesWithDiffContent.push({
          path: file.path,
          status: file.status as DiffReportFile['status'],
          diff: diffResult.success ? diffResult.diff || '' : '',
        });
      }
      
      const otherFiles = commitFiles.filter(f => 
        !f.path.includes('data/product/') && 
        !f.path.includes('user-stories') &&
        !f.path.includes('issues.md')
      );
      
      for (const file of otherFiles) {
        filesWithDiffContent.push({
          path: file.path,
          status: file.status as DiffReportFile['status'],
          diff: '',
        });
      }
      
      setFilesWithDiffs(filesWithDiffContent);
      setStep('options');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file diffs');
    } finally {
      setLoadingDiffs(false);
    }
  };

  const performExport = () => {
    if (!selectedCommit || filesWithDiffs.length === 0) {
      setError('No commit selected or no files to export.');
      return;
    }

    setExporting(true);
    setError(null);
    setStep('exporting');

    try {
      const commitData: DiffReportCommit = {
        hash: selectedCommit.hash,
        shortHash: selectedCommit.shortHash,
        message: selectedCommit.message,
        author: selectedCommit.author,
        date: selectedCommit.date,
      };

      const result = exportDiffReportToPdf(commitData, filesWithDiffs, options);

      setExportResult(result);
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
      setStep('options');
    } finally {
      setExporting(false);
    }
  };

  const handleComplete = () => {
    onExportComplete();
    onClose();
  };

  const productDataFileCount = commitFiles.filter(f => 
    f.path.includes('data/product/') || 
    f.path.includes('user-stories') ||
    f.path.includes('issues.md')
  ).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <GitCommit className="w-5 h-5" />
            Generate Diff Report
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Select Commit Step */}
        {step === 'select' && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select a Commit</h3>
              <p className="text-sm text-gray-600">
                Choose a commit from the history to generate a diff report for user stories and issues.
              </p>
            </div>

            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {loadingCommits ? (
                <div className="flex items-center justify-center py-8">
                  <Loader size={24} className="animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading commits...</span>
                </div>
              ) : commits.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No commits found in this repository
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {commits.map(commit => (
                    <button
                      key={commit.hash}
                      onClick={() => handleSelectCommit(commit)}
                      disabled={loadingFiles}
                      className={`w-full p-3 text-left hover:bg-blue-50 transition-colors ${
                        selectedCommit?.hash === commit.hash ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      } ${loadingFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <Clock size={16} className="text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate" title={commit.message}>
                            {commit.message}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                              {commit.shortHash}
                            </span>
                            <span>•</span>
                            <span>{commit.author.name}</span>
                            <span>•</span>
                            <span>{formatCommitDate(commit.date)}</span>
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-400 mt-1" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {loadingFiles && (
              <div className="mt-4 flex items-center justify-center text-gray-500">
                <Loader size={16} className="animate-spin mr-2" />
                Loading commit files...
              </div>
            )}
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && selectedCommit && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Commit Preview</h3>
              <p className="text-sm text-gray-600">
                Review the files changed in this commit before generating the report.
              </p>
            </div>

            {/* Commit Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <GitCommit size={16} className="text-gray-600" />
                <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">
                  {selectedCommit.shortHash}
                </span>
              </div>
              <p className="font-medium text-gray-900">{selectedCommit.message}</p>
              <p className="text-sm text-gray-500 mt-1">
                by {selectedCommit.author.name} on {new Date(selectedCommit.date).toLocaleString()}
              </p>
            </div>

            {/* Files Changed */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Files Changed ({commitFiles.length})
                {productDataFileCount > 0 && (
                  <span className="ml-2 text-blue-600">
                    ({productDataFileCount} product data file{productDataFileCount !== 1 ? 's' : ''})
                  </span>
                )}
              </h4>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {commitFiles.map(file => (
                  <div key={file.path} className="flex items-center gap-2 p-2">
                    {getFileStatusIcon(file.status)}
                    <span className="text-sm text-gray-700 truncate flex-1">{file.path}</span>
                    {(file.path.includes('data/product/') || 
                      file.path.includes('user-stories') || 
                      file.path.includes('issues.md')) && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        product data
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {productDataFileCount === 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  This commit does not contain any user story or issue changes.
                  The report will only include the file summary.
                </p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setSelectedCommit(null);
                  setCommitFiles([]);
                  setStep('select');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleProceedToOptions}
                disabled={loadingDiffs}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loadingDiffs && <Loader className="w-4 h-4 animate-spin" />}
                Continue to Options
              </button>
            </div>
          </div>
        )}

        {/* Options Step */}
        {step === 'options' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Report Options
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Customize your diff report PDF.
              </p>
            </div>

            <div className="space-y-6">
              {/* Report Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Title</label>
                <input
                  type="text"
                  value={options.title}
                  onChange={(e) => setOptions({ ...options, title: e.target.value })}
                  placeholder="Diff Report"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  type="text"
                  value={options.productName}
                  onChange={(e) => setOptions({ ...options, productName: e.target.value })}
                  placeholder="My Product"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Page Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page Size</label>
                  <select
                    value={options.pageSize}
                    onChange={(e) => setOptions({ ...options, pageSize: e.target.value as 'a4' | 'letter' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="a4">A4</option>
                    <option value="letter">Letter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <select
                    value={options.orientation}
                    onChange={(e) => setOptions({ ...options, orientation: e.target.value as 'portrait' | 'landscape' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
              </div>

              {/* Content Options */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Content Options</h4>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="includeFileSummary"
                    checked={options.includeFileSummary}
                    onChange={(e) => setOptions({ ...options, includeFileSummary: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="includeFileSummary" className="text-sm text-gray-700">
                    Include File Summary
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="includeRawDiff"
                    checked={options.includeRawDiff}
                    onChange={(e) => setOptions({ ...options, includeRawDiff: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="includeRawDiff" className="text-sm text-gray-700">
                    Include Detailed Diff Output
                  </label>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Export Summary</h4>
                <p className="text-sm text-blue-800">
                  Commit: <span className="font-mono">{selectedCommit?.shortHash}</span>
                  <br />
                  Files: {filesWithDiffs.length} total, {filesWithDiffs.filter(f => 
                    f.path.includes('data/product/') || 
                    f.path.includes('user-stories') ||
                    f.path.includes('issues.md')
                  ).length} product data
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('preview')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={performExport}
                disabled={exporting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {exporting && <Loader className="w-4 h-4 animate-spin" />}
                <Download className="w-4 h-4" />
                Generate PDF Report
              </button>
            </div>
          </div>
        )}

        {/* Exporting Step */}
        {step === 'exporting' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Generating Report...</p>
            <p className="text-sm text-gray-600 mt-2">Creating your diff report PDF</p>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && exportResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Report Generated!</h3>
              <p className="text-gray-600">
                Your file has been downloaded: <span className="font-medium">{exportResult.filename}</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {exportResult.userStoryCount}
                </p>
                <p className="text-sm text-purple-900">User Story Changes</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {exportResult.issueCount}
                </p>
                <p className="text-sm text-blue-900">Issue Changes</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep('select');
                  setSelectedCommit(null);
                  setCommitFiles([]);
                  setFilesWithDiffs([]);
                  setExportResult(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Generate Another Report
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
